import { Button, ButtonThemesEnum } from '@propertyfinder/button';
import { TextField } from '@propertyfinder/text-field';
import { Textarea } from '@propertyfinder/textarea';
import preact, { Fragment, FunctionalComponent, h } from 'preact';

import { domClassMerge } from 'common/helper/dom/class-merge';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { ClaimTransactionComponent } from 'common/module/property-report/claim-transaction/component';
import { FormPropertyReportParamsInterface } from 'common/module/property-report/params.interface';
import { PropertyReportSectionType } from 'common/module/property-report/section.type';
import { FormPropertyReportTemplatePropsInterface } from 'common/module/property-report/template-props.interface';
import { PropertyReportUploadTemplate } from 'common/module/property-report/upload/template';
import { ModalComponent } from 'library/modal/component';

import { FormPropertyReportViewStore } from './view-store';

export const FormPropertyReportTemplate: FunctionalComponent<FormPropertyReportTemplatePropsInterface> = (
  props
): preact.JSX.Element => {
  /**
   * Render a field specific error message
   */
  function renderError(fieldName: keyof FormPropertyReportParamsInterface): preact.JSX.Element {
    if (props.fieldErrors[fieldName]) {
      return <div className='input__message input__message--invalid'>{props.fieldErrors[fieldName]}</div>;
    }
  }

  /**
   * Render the report submitted form
   */
  function renderSuccessMessage(): preact.JSX.Element {
    return (
      <div className='success-message'>
        <span
          className='svg'
          dangerouslySetInnerHTML={{
            __html: `
            <svg viewBox='0 0 32 32' class='success-message__icon'>
              <use xlink:href='#svg-success-icon'></use>
            </svg>
          `,
          }}
        />
        <div className='success-message__text'>
          <span>{i18nTranslate('Thank you')}</span>
          <span>{i18nTranslate('Property has been reported.')}</span>
        </div>
      </div>
    );
  }

  /**
   * Renders bare report section
   */
  function renderDefaultReportSection(): preact.JSX.Element {
    return (
      <Fragment>
        {props.showAttachment && (
          <div className='property-report-2 report-property-form__input-container'>
            <PropertyReportUploadTemplate
              file={props.params.attachment}
              title={i18nTranslate('Upload file (Maximum upload file size is {size} MB)').replace(
                '{size}',
                String(FormPropertyReportViewStore.MAX_FILE_UPLOAD_SIZE)
              )}
              onUploadFile={(e) => props.onChangeFile(e, 'attachment')}
              onDocumentClear={props.clearAttachment}
              fileInputRef={props.fileInputRef}
              onBlurFile={(e) => props.onBlur(e, 'attachment')}
            />
            {renderError('attachment')}
          </div>
        )}
        <div className='report-property-form__input-container contact-form__text-area'>
          <Textarea
            htmlAttributes={{ 'data-testid': 'property-report-form-message' }}
            value={props.params.message}
            placeholder={i18nTranslate('Additional Comments')}
            onChange={(value): void => props.onChange({ target: { value } } as unknown as Event, 'message')}
            onBlur={(e: Event): void => props.onBlur(e, 'message')}
            errorText={props.fieldErrors.message}
          />
        </div>
        <div className='report-property-form__input-container authentication__error'>
          {props.fieldErrors.captcha_token}
        </div>
        <div className='report-property-form__button-area'>
          <Button type='submit' theme={ButtonThemesEnum.PRIMARY} loading={props.status.submitting}>
            {i18nTranslate('Send Report')}
          </Button>
        </div>
      </Fragment>
    );
  }

  /**
   * Renders claim my transaction report section
   */
  function renderClaimTransactionReportSection(): preact.JSX.Element {
    return (
      <ClaimTransactionComponent
        state={props.claimTransactionParams}
        claimTransactionAttachments={props.claimTransactionAttachments}
        onChange={props.onInputChange}
        onValidate={props.checkEligible}
        onGoBack={(): void => props.toggleFormSelectionsVisible(true, 'validate')}
        onChangeCmtFile={props.onChangeCmtFile}
        onDocumentClear={props.onDocumentClear}
        stage={props.stage}
        cmtFieldErrors={props.cmtFieldErrors}
        eligibilityCheckStatus={props.eligibilityCheckStatus}
        onSubmitClaimTransaction={props.onSubmitClaimTransaction}
        status={props.status}
        agentInformation={props.agentInformation}
        propertyId={props.model.id}
      />
    );
  }

  /**
   * Render the report form
   */
  function renderForm(): JSX.Element {
    const disableReasonSelection = !props.params.reporter_type && props.showReporterType;
    return (
      <form onSubmit={props.onSubmit} className='contact-form__form' noValidate>
        {props.formSelectionsVisible && (
          <Fragment>
            <div className='report-property-form__input-container'>
              {props.emailRequired && (
                <TextField
                  htmlAttributes={{
                    type: 'email',
                  }}
                  placeholder={i18nTranslate('authentication2|Email')}
                  errorText={props.fieldErrors.email}
                  value={props.params.email}
                  onInput={(e: InputEvent): void => props.onChange(e, 'email')}
                  onBlur={(e: Event): void => props.onBlur(e, 'email')}
                />
              )}
            </div>
            {props.showReporterType && (
              <div className='report-property-form__input-container'>
                <div className='select-dropdown select-dropdown--small'>
                  <select
                    name='reporter-type'
                    className={`
                select-dropdown__item
                ${props.fieldErrors.reporter_type ? 'select-dropdown--invalid' : ''}`}
                    value={props.params.reporter_type ? String(props.params.reporter_type) : ''}
                    onChange={(e) => props.onChangeReporterType(e)}
                    onBlur={(e) => props.onBlur(e, 'reporter_type')}
                  >
                    <option value=''>{i18nTranslate('Select user type')}</option>
                    {props.inputs.reporter_type.map((reporter) => (
                      <option value={String(reporter.value)}>{reporter.label}</option>
                    ))}
                  </select>
                </div>
                {renderError('reporter_type')}
              </div>
            )}
            <div
              className={domClassMerge('report-property-form__input-container', {
                'report-property-form__input-container--disabled': disableReasonSelection,
              })}
            >
              <div className='select-dropdown select-dropdown--small'>
                <select
                  disabled={props.showReporterType ? !props.params.reporter_type.length : false}
                  name='reason'
                  className={`
              select-dropdown__item
              ${props.fieldErrors.reason_id ? 'select-dropdown__item--invalid' : ''}`}
                  value={props.params.reason_id ? String(props.params.reason_id) : ''}
                  onChange={(e) => props.onChange(e, 'reason_id')}
                  onBlur={(e) => props.onBlur(e, 'reason_id')}
                >
                  <option value=''>{i18nTranslate('Select a reason')}</option>
                  {props.inputs.reasons.map((reason) => (
                    <option value={String(reason.value)}>{reason.label}</option>
                  ))}
                </select>
              </div>
              {renderError('reason_id')}
            </div>
          </Fragment>
        )}
        {reportSectionMap[props.reportSectionState]}
      </form>
    );
  }

  const reportSectionMap: Record<PropertyReportSectionType, preact.JSX.Element> = {
    default: renderDefaultReportSection(),
    'claim-validate': renderClaimTransactionReportSection(),
  };

  return (
    <ModalComponent
      title={i18nTranslate('ReportProperty_ModalTitle')}
      onClose={props.onClickClose}
      leftSlot={
        props.status.submitted
          ? undefined
          : {
              description: i18nTranslate('ReportProperty_ModalDesc'),
              image: <img src={__('assets/common/report-property.svg')} width={164} height={113} />,
            }
      }
    >
      {!!props.error && <div className='contact-form__error--general'>{props.error}</div>}
      {props.status.submitted && renderSuccessMessage()}
      {!props.status.submitted && renderForm()}
    </ModalComponent>
  );
};
